"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { ChevronLeft, ChevronRight, Expand, X } from "lucide-react";

type GalleryPhoto = { src: string; caption: string };

/*
 * Event photo carousel: swipe or use the arrows, dots show there is more, and
 * clicking a photo opens a full-screen viewer (arrow keys, Esc to close).
 */
export const PhotoCarousel: React.FC<{
  photos: GalleryPhoto[];
  label: string;
}> = ({ photos, label }) => {
  const track = useRef<HTMLDivElement | null>(null);
  const [index, setIndex] = useState(0);
  const [viewer, setViewer] = useState<number | null>(null);
  // Furthest slide reached, so photos already loaded stay rendered.
  const [seen, setSeen] = useState(0);
  const last = photos.length - 1;

  const indexRef = useRef(0);
  indexRef.current = index;
  // Keep the same photo in view when the carousel width changes (rotation, resize).
  useEffect(() => {
    const el = track.current;
    if (!el || !("ResizeObserver" in window)) return;
    const observer = new ResizeObserver(() => {
      el.scrollTo({
        left: indexRef.current * el.clientWidth,
        behavior: "auto",
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const goTo = useCallback((i: number) => {
    const el = track.current;
    if (!el) return;
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    el.scrollTo({
      left: i * el.clientWidth,
      behavior: reduce ? "auto" : "smooth",
    });
  }, []);

  const onScroll = () => {
    const el = track.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    setIndex(i);
    setSeen((s) => Math.max(s, i));
  };

  const arrow =
    "absolute top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-wcd-ink shadow-[0_4px_16px_rgba(0,0,0,0.18)] backdrop-blur transition hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#F97316] disabled:pointer-events-none disabled:opacity-0 md:flex md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100";

  return (
    <div role="region" aria-roledescription="carousel" aria-label={label}>
      <div className="group relative overflow-hidden rounded-[24px]">
        <div
          ref={track}
          onScroll={onScroll}
          className="flex snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {photos.map((photo, i) => (
            // Only the current photo and its neighbours download; the rest load as you swipe.
            <button
              key={photo.src}
              type="button"
              onClick={() => setViewer(i)}
              tabIndex={i === index ? 0 : -1}
              aria-label={`Open photo ${i + 1} of ${photos.length}: ${photo.caption}`}
              className="relative aspect-[3/2] w-full shrink-0 snap-center cursor-zoom-in focus-visible:outline-none"
            >
              {i <= Math.max(index, seen) + 1 && (
                <Image
                  src={photo.src}
                  alt={photo.caption}
                  fill
                  sizes="(min-width: 1024px) 648px, calc(100vw - 40px)"
                  quality={80}
                  className="object-cover"
                />
              )}
            </button>
          ))}
        </div>

        <button
          type="button"
          aria-label="Previous photo"
          onClick={() => goTo(index - 1)}
          disabled={index === 0}
          className={`${arrow} left-4`}
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Next photo"
          onClick={() => goTo(index + 1)}
          disabled={index === last}
          className={`${arrow} right-4`}
        >
          <ChevronRight className="h-5 w-5" aria-hidden="true" />
        </button>

        <span className="pointer-events-none absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-xs font-medium tabular-nums text-white backdrop-blur">
          <Expand className="h-3 w-3" aria-hidden="true" />
          {index + 1} / {photos.length}
        </span>
      </div>

      <div className="mt-4 flex items-center justify-between gap-4">
        <p
          className="min-w-0 truncate text-xs text-wcd-caption"
          aria-live="polite"
        >
          {photos[index]?.caption}
        </p>
        <div className="flex shrink-0 items-center gap-1.5">
          {photos.map((photo, i) => (
            // Only the current photo and its neighbours download; the rest load as you swipe.
            <button
              key={photo.src}
              type="button"
              aria-label={`Show photo ${i + 1}`}
              aria-current={i === index || undefined}
              onClick={() => goTo(i)}
              className="flex h-6 items-center"
            >
              <span
                className={`block h-1.5 rounded-full transition-all duration-300 motion-reduce:transition-none ${
                  i === index
                    ? "w-5 bg-wcd-ink"
                    : "w-1.5 bg-wcd-divider hover:bg-wcd-neutral"
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      {viewer !== null && (
        <PhotoViewer
          photos={photos}
          start={viewer}
          onClose={(i) => {
            setViewer(null);
            goTo(i);
            // Return focus to the photo the viewer was opened from.
            requestAnimationFrame(() =>
              (track.current?.children[i] as HTMLElement | undefined)?.focus({
                preventScroll: true,
              }),
            );
          }}
        />
      )}
    </div>
  );
};

/** Full-screen viewer. Closes on Esc, backdrop click or the X; arrows and keys page. */
const PhotoViewer: React.FC<{
  photos: GalleryPhoto[];
  start: number;
  onClose: (index: number) => void;
}> = ({ photos, start, onClose }) => {
  const [i, setI] = useState(start);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const n = photos.length;
  const prev = useCallback(() => setI((v) => (v - 1 + n) % n), [n]);
  const next = useCallback(() => setI((v) => (v + 1) % n), [n]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose(i);
      if (e.key === "Tab" && dialogRef.current) {
        // Keep focus inside the viewer.
        const items = dialogRef.current.querySelectorAll<HTMLElement>("button");
        const visible = Array.from(items).filter((b) => b.offsetParent !== null);
        const first = visible[0];
        const lastEl = visible[visible.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          lastEl?.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          first?.focus();
        }
      }
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [i, onClose, prev, next]);

  useEffect(() => closeRef.current?.focus(), []);

  const nav =
    "flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#F97316]";

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Event photos"
      className="fixed inset-0 z-[100] flex flex-col bg-[#0F0708]/95 p-4 md:p-8"
      onClick={(e) => e.target === e.currentTarget && onClose(i)}
    >
      <div className="flex items-center justify-between text-sm text-white/70">
        <span className="tabular-nums">
          {i + 1} / {n}
        </span>
        <button
          ref={closeRef}
          type="button"
          aria-label="Close photos"
          onClick={() => onClose(i)}
          className={nav}
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      <div
        className="flex min-h-0 flex-1 items-center justify-center gap-4"
        onClick={(e) => e.target === e.currentTarget && onClose(i)}
      >
        <button
          type="button"
          aria-label="Previous photo"
          onClick={prev}
          className={`${nav} hidden shrink-0 md:flex`}
        >
          <ChevronLeft className="h-6 w-6" aria-hidden="true" />
        </button>
        <figure className="flex h-full min-w-0 max-w-[1200px] flex-1 flex-col items-center justify-center gap-4">
          <div className="relative h-full max-h-[78vh] w-full">
            <Image
              key={photos[i].src}
              src={photos[i].src}
              alt={photos[i].caption}
              fill
              sizes="(min-width: 1334px) 1200px, calc(100vw - 32px)"
              quality={90}
              className="object-contain"
            />
          </div>
          <figcaption className="text-sm text-white/70">
            {photos[i].caption}
          </figcaption>
        </figure>
        <button
          type="button"
          aria-label="Next photo"
          onClick={next}
          className={`${nav} hidden shrink-0 md:flex`}
        >
          <ChevronRight className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
      <div className="flex justify-center gap-3 pt-4 md:hidden">
        <button
          type="button"
          aria-label="Previous photo"
          onClick={prev}
          className={nav}
        >
          <ChevronLeft className="h-6 w-6" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Next photo"
          onClick={next}
          className={nav}
        >
          <ChevronRight className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
    </div>,
    document.body,
  );
};
